import { deepSignal } from "deepsignal";
import { Minus, Plus } from "flowbite-react-icons/outline";
import { Eye, EyeSlash } from "flowbite-react-icons/solid";
import { useState } from "preact/hooks";
import PropTypes from "prop-types";
import VideoBackground from "@/components/shared/video-background.jsx";
import { useDeepSignalFromLocalStorage } from "@/util/deepSignals.js";

const scales = [
  "scale-30",
  "scale-35",
  "scale-40",
  "scale-45",
  "scale-50",
  "scale-60",
  "scale-65",
  "scale-75",
  "scale-90",
  "scale-95",
  "scale-100",
];

const config = deepSignal({
  overlayBackground: null,
});

ScaledOverlayWrapper.propTypes = {
  children: PropTypes.node,
  defaultScale: PropTypes.oneOf(scales),
  textCenter: PropTypes.bool,
  className: PropTypes.string,
  addPadding: PropTypes.bool,
  allowScaling: PropTypes.bool,
  innerClassName: PropTypes.string,
  addPosition: PropTypes.string,
  currentScale: PropTypes.oneOf(scales),
  justifyCenter: PropTypes.bool,
};
export default function ScaledOverlayWrapper({
  children,
  className = "",
  defaultScale = "scale-100",
  addPadding = true,
  allowScaling = true,
  innerClassName = "",
  addPosition = "",
  currentScale = defaultScale,
  justifyCenter = true,
}) {
  useDeepSignalFromLocalStorage(config);

  const [currentScaleIndex, setCurrentScaleIndex] = useState(scales.indexOf(defaultScale) || 0);

  const [showBackground, setShowBackground] = useState(true);
  const canScaleDown = currentScaleIndex > 0;
  const canScaleUp = currentScaleIndex < scales.length - 1;
  const videoURL = import.meta.env.VITE_ANIMATED_BACKGROUND_URI ?? "";

  const videoURLs = {
    webm: videoURL.replace(".mp4", ".webm"),
    mp4: videoURL,
  };

  function scaleUp() {
    setCurrentScaleIndex(currentScaleIndex + 1);
  }

  function scaleDown() {
    setCurrentScaleIndex(currentScaleIndex - 1);
  }

  function toggleBackground() {
    setShowBackground(!showBackground);
  }

  let scalingBlock;

  if (allowScaling) {
    scalingBlock = (
      <div className="flex absolute left-0 px-2 py-2 bottom-0 items-center select-none z-30">
        <button
          type="button"
          className="btn btn-circle btn-sm mr-1 btn-secondary shadow-xl"
          onClick={scaleDown}
          disabled={!canScaleDown}
        >
          <Minus />
        </button>
        <button
          type="button"
          className="btn btn-circle btn-sm mr-2 btn-secondary shadow-xl"
          onClick={scaleUp}
          disabled={!canScaleUp}
        >
          <Plus />
        </button>
      </div>
    );
  }

  return (
    <div
      className={
        className.length > 0 ? `w-full transition-all ${className}` : "transition-all w-full"
      }
    >
      <div className="rounded-lg border-base-300 border w-full overflow-hidden relative bg-preview">
        <div
          className={`flex justify-start ${innerClassName ? innerClassName : "h-[550px]"} ${addPadding ? "px-0 py-16" : "p-0"} overflow-hidden relative`}
        >
          <div
            className={`flex ${addPadding ? "px-2 pb-5 select-none" : "select-none"} h-full w-full transition-transform ${currentScale} ${addPosition} ${justifyCenter ? "justify-center items-center" : ""}  z-20`}
          >
            {children}
          </div>
          <div className="absolute top-0 bottom-0 left-0 right-0 z-10">
            <VideoBackground video={videoURLs} overlay={true} show={showBackground} />
          </div>
        </div>
        {scalingBlock}
        <div
          className="flex absolute right-0 z-50 px-2 py-2 bottom-0 items-center select-none tooltip tooltip-left"
          data-tip={showBackground ? "Hide background" : "Show background"}
        >
          <button
            type="button"
            className={`btn btn-circle btn-sm bg-white btn-ghost ${showBackground ? "text-primary" : "text-secondary"}`}
            onClick={toggleBackground}
          >
            {showBackground ? <EyeSlash /> : <Eye />}
          </button>
        </div>
      </div>
    </div>
  );
}
